<template>
  <div class="fts-settings-page fts-portals">
    <header class="fts-settings-head">
      <div>
        <h1>Portals</h1>
        <p>
          Web pages Inistate publishes for your workspace on e.inistate.com. Members open them from the side
          menu or the mobile app, signed in as themselves, with their own rights.
        </p>
      </div>
      <button
        type="button"
        class="fts-settings-btn is-primary"
        ref="addButton"
        v-if="ready"
        @click="openAdd"
      >
        <font-awesome-icon :icon="['fas', 'plus']" />
        <span>Add portal</span>
      </button>
    </header>

    <div class="fts-settings-loading" v-if="loading">
      <fts-spinner :size="16" />
      <span>Loading&hellip;</span>
    </div>

    <!-- The feature is off on this server (404 not_enabled): say so, rather than a broken page. -->
    <section class="fts-settings-card fts-portals-off" v-else-if="notEnabled">
      <div>
        <h2>Portal links are not switched on for this server.</h2>
        <p class="fts-settings-card-sub">
          Inistate switches them on server by server. Once they are on, the portals you add here appear in
          your members&rsquo; side menu and mobile app.
        </p>
      </div>
    </section>

    <div class="fts-settings-banner is-warn" role="alert" v-else-if="loadError">
      <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
      <span>
        {{ loadError }}
        <a href="#" class="fts-portals-retry" @click.prevent="load">Try again</a>
      </span>
    </div>

    <template v-else-if="ready">
      <div class="fts-settings-banner is-danger" role="alert" v-if="error">
        <font-awesome-icon :icon="['fas', 'exclamation-circle']" />
        <span>{{ error }}</span>
      </div>

      <section class="fts-settings-card" aria-label="Portals">
        <p class="fts-portals-empty" v-if="!rows.length">
          No portals yet. Add one with the address your Inistate consultant gives you.
        </p>

        <div class="fts-portals-scroll" v-else>
          <table class="fts-settings-table fts-portals-table">
            <thead>
              <tr>
                <th>Portal</th>
                <th>Address</th>
                <th>Who sees it</th>
                <th>Shows on</th>
                <th>Status</th>
                <th>Last opened</th>
                <th class="is-right"><span class="fts-portals-sr">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in rows" :key="row.id" :data-portal-id="row.id">
                <td>
                  <span class="fts-portals-name">
                    <span class="fts-portals-name-icon" aria-hidden="true">
                      <font-awesome-icon v-if="row.icon.fa" :icon="row.icon.fa" fixed-width />
                      <template v-else>{{ row.icon.emoji }}</template>
                    </span>
                    <strong>{{ row.name }}</strong>
                  </span>
                </td>
                <td class="is-muted fts-portals-address">{{ row.address }}</td>
                <td>{{ row.audience }}</td>
                <td class="fts-portals-nowrap">{{ row.showsOn }}</td>
                <td>
                  <span class="fts-settings-pill" :class="{ 'is-ok': row.enabled }">{{ row.enabled ? "On" : "Off" }}</span>
                  <span class="fts-portals-unchecked" v-if="!row.checked" title="Inistate's server could not reach this page to check it when it was saved">Not checked</span>
                </td>
                <td class="is-muted fts-portals-nowrap">{{ row.lastOpened }}</td>
                <td class="is-right">
                  <fts-dropdown-select
                    :ref="'menu-' + row.id"
                    append-to-body
                    strategy-fixed
                    placement="bottom-end"
                    :disabled="busyId === row.id"
                    @show="focusMenu"
                    @selected="(action) => rowAction(row, action)"
                  >
                    <template #toggle>
                      <button
                        type="button"
                        class="fts-portals-more"
                        :aria-label="'More actions for ' + row.name"
                        aria-haspopup="menu"
                        :disabled="busyId === row.id"
                      >
                        <font-awesome-icon :icon="['fas', 'ellipsis-h']" />
                      </button>
                    </template>
                    <template #menu="{ select, defaultOptionClass }">
                      <div class="fts-portals-menu" role="menu" @keydown.esc.stop="closeMenu(row)">
                        <button type="button" role="menuitem" :class="defaultOptionClass" @click="select('edit')">Edit</button>
                        <button type="button" role="menuitem" :class="defaultOptionClass" @click="select('toggle')">
                          {{ row.enabled ? "Switch off" : "Switch on" }}
                        </button>
                        <button type="button" role="menuitem" :class="defaultOptionClass" @click="select('end')">End open sessions</button>
                        <button type="button" role="menuitem" :class="[defaultOptionClass, 'fts-portals-menu-danger']" @click="select('delete')">Delete</button>
                      </div>
                    </template>
                  </fts-dropdown-select>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- Pages being tried on an admin's own computer (docs/integration-portal-claude-design-proposal.md
           §3.4). Not drawn where the server has debugging off. -->
      <section class="fts-settings-card fts-portals-debug" aria-labelledby="fts-portals-debug-title" v-if="!debug.off">
        <div>
          <h2 id="fts-portals-debug-title">Debug sessions</h2>
          <p class="fts-settings-card-sub">
            Portal pages an admin is trying on their own computer, with their own Inistate session. Each one
            ends by itself at the time shown, or when that admin signs out.
          </p>
        </div>

        <div class="fts-settings-loading" v-if="debug.loading">
          <fts-spinner :size="16" />
          <span>Loading&hellip;</span>
        </div>
        <div class="fts-settings-banner is-warn" role="alert" v-else-if="debug.error">
          <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
          <span>
            {{ debug.error }}
            <a href="#" class="fts-portals-retry" @click.prevent="loadDebug">Try again</a>
          </span>
        </div>
        <p class="fts-portals-empty" v-else-if="!debugRows.length">No debug sessions.</p>
        <div class="fts-portals-scroll" v-else>
          <table class="fts-settings-table fts-portals-table fts-portals-debug-table">
            <thead>
              <tr>
                <th>Who</th>
                <th>Port</th>
                <th>Until</th>
                <th class="is-right"><span class="fts-portals-sr">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in debugRows" :key="row.id" :data-grant-id="row.id">
                <td><strong>{{ row.who }}</strong></td>
                <td class="fts-portals-nowrap">{{ row.port }}</td>
                <td class="fts-portals-nowrap">{{ row.until }}</td>
                <td class="is-right">
                  <button
                    type="button"
                    class="fts-settings-btn"
                    :aria-label="'End the debug session of ' + row.who + ' on port ' + row.port"
                    :disabled="debug.busyId === row.id"
                    @click="endDebug(row)"
                  >
                    {{ debug.busyId === row.id ? "Ending…" : "End" }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>

    <!-- Add / edit. Drawn inside the settings frame so its tokens (and the dark theme) apply. -->
    <div class="fts-portals-overlay" v-if="dialog.open" @mousedown.self="closeDialog">
      <div
        class="fts-portals-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="fts-portals-dialog-title"
        ref="dialog"
        @keydown.esc.stop="closeDialog"
        @keydown.tab="trapFocus"
      >
        <div class="fts-portals-dialog-head">
          <h2 id="fts-portals-dialog-title">{{ dialog.id ? "Edit portal" : "Add portal" }}</h2>
          <button type="button" class="fts-portals-close" aria-label="Close" @click="closeDialog">
            <font-awesome-icon :icon="['fas', 'times']" />
          </button>
        </div>

        <form class="fts-portals-form" novalidate @submit.prevent="save">
          <div class="fts-settings-field">
            <span class="fts-settings-field-label" id="fts-portals-icon-label">Icon</span>
            <div class="fts-portals-icons" role="radiogroup" aria-labelledby="fts-portals-icon-label">
              <button
                v-for="icon in icons"
                :key="icon.name"
                type="button"
                role="radio"
                class="fts-portals-icon-choice"
                :class="{ 'is-on': form.icon === icon.name }"
                :aria-checked="String(form.icon === icon.name)"
                :aria-label="icon.label"
                :title="icon.label"
                @click="form.icon = icon.name"
              >
                <font-awesome-icon :icon="[icon.prefix, icon.name]" fixed-width />
              </button>
            </div>
            <span class="fts-settings-note is-error" v-if="fieldErrors.icon">{{ fieldErrors.icon }}</span>
          </div>

          <div class="fts-settings-field">
            <label class="fts-settings-field-label" for="fts-portals-name">Name</label>
            <input
              id="fts-portals-name"
              ref="nameInput"
              type="text"
              class="fts-settings-input fts-portals-input"
              autocomplete="off"
              :maxlength="nameMax"
              :aria-invalid="fieldErrors.name ? 'true' : null"
              v-model="form.name"
            />
            <span class="fts-settings-note is-error" v-if="fieldErrors.name">{{ fieldErrors.name }}</span>
          </div>

          <div class="fts-settings-field">
            <label class="fts-settings-field-label" for="fts-portals-url">Address</label>
            <div class="fts-portals-address-row">
              <input
                id="fts-portals-url"
                type="url"
                class="fts-settings-input fts-portals-input"
                autocomplete="off"
                spellcheck="false"
                placeholder="https://e.inistate.com/…"
                aria-describedby="fts-portals-url-help fts-portals-url-check"
                :aria-invalid="addressInvalid ? 'true' : null"
                v-model.trim="form.url"
                @input="onAddressInput"
                @blur="onAddressBlur"
              />
              <button
                type="button"
                class="fts-settings-btn"
                ref="checkButton"
                :disabled="check.state === 'checking' || !form.url"
                @click="checkAddress(true)"
              >
                {{ check.state === "checking" ? "Checking…" : "Check" }}
              </button>
            </div>
            <span class="fts-settings-note" id="fts-portals-url-help">{{ addressHelpText }}</span>
            <div id="fts-portals-url-check" aria-live="polite">
              <div class="fts-portals-check" v-if="check.state === 'ok' && !fieldErrors.url">
                <font-awesome-icon :icon="['fas', 'check-circle']" />
                <span>This page is set up as an Inistate portal.</span>
              </div>
              <!-- The server could not reach the page (network, TLS): it can still be saved, marked as not checked. -->
              <div class="fts-portals-check is-warn" v-else-if="check.state === 'unchecked' && !fieldErrors.url">
                <font-awesome-icon :icon="['fas', 'exclamation-circle']" />
                <span>{{ check.message }}</span>
              </div>
              <span class="fts-settings-note is-error" v-else-if="addressInvalid">
                {{ fieldErrors.url || check.message }}
              </span>
            </div>
          </div>

          <fieldset class="fts-portals-fieldset">
            <legend class="fts-settings-field-label">Who sees it</legend>
            <label class="fts-portals-choice">
              <input type="radio" name="fts-portals-visibility" value="everyone" v-model="form.visibility" />
              <span>Everyone in this workspace</span>
            </label>
            <label class="fts-portals-choice">
              <input type="radio" name="fts-portals-visibility" value="admins" v-model="form.visibility" />
              <span>Workspace admins only</span>
            </label>
          </fieldset>

          <fieldset class="fts-portals-fieldset">
            <legend class="fts-settings-field-label">Show on</legend>
            <label class="fts-portals-choice">
              <input type="checkbox" v-model="form.showOnWeb" />
              <span>Web: opens in a new tab</span>
            </label>
            <label class="fts-portals-choice">
              <input type="checkbox" v-model="form.showOnApp" />
              <span>Mobile app: opens full screen</span>
            </label>
            <span class="fts-settings-note is-error" v-if="fieldErrors.platforms">{{ fieldErrors.platforms }}</span>
          </fieldset>

          <div class="fts-settings-banner is-danger" role="alert" v-if="dialog.error">
            <font-awesome-icon :icon="['fas', 'exclamation-circle']" />
            <span>{{ dialog.error }}</span>
          </div>

          <div class="fts-portals-dialog-foot">
            <button type="button" class="fts-settings-btn" v-if="canTest" @click="openToTest">
              <font-awesome-icon :icon="['fas', 'up-right-from-square']" />
              <span>Open to test</span>
            </button>
            <span class="fts-portals-spacer"></span>
            <button type="button" class="fts-settings-btn" @click="closeDialog">Cancel</button>
            <button type="submit" class="fts-settings-btn is-primary" ref="saveButton" :disabled="dialog.saving">
              {{ dialog.saving ? "Saving…" : "Save" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Settings › Portals (docs/integration-portal-link-proposal.md §4.3, §6 in the SVN repo; alpha).
 *
 * A workspace admin registers pages Inistate publishes for the workspace on e.inistate.com (the
 * server allows exact origins only and requires the page to opt in). Members then see them in the
 * side menu and the mobile app and open them signed in as themselves. Everything here is checked
 * again by the server; the form only asks first so the answer comes sooner.
 *
 * Changing the address, narrowing who sees it or where it shows, switching it off and deleting it
 * end the portal's open sessions on the server; so does "End open sessions".
 */
import FTSSpinner from "../../elements/FTSSpinner";
import FTSDropdownSelect from "../../elements/FTSDropdownSelect";
import { clientStore } from "../../store/clientStore";
import api from "../../portals/portalApi";
import { PORTAL_ICONS, DEFAULT_PORTAL_ICON, portalIcon, portalHref, bootstrapPortals } from "../../portals/portalNav";
import { addLinkOf, portalAt } from "../../portals/portalSetupLink";
import {
  PORTAL_NAME_MAX,
  addressHelp,
  addressLabel,
  audienceText,
  showsOnText,
  lastOpenedText,
  untilText,
  originPort,
  fieldOfError,
  errorMessage,
  errorCode,
  formProblems,
} from "../../portals/portalFormat";

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

function emptyDebug() {
  return { loading: false, loaded: false, off: false, error: "", grants: [], busyId: "" };
}

function emptyForm() {
  return {
    icon: DEFAULT_PORTAL_ICON,
    name: "",
    url: "",
    visibility: "everyone",
    showOnWeb: true,
    showOnApp: true,
  };
}

function emptyCheck() {
  return { state: "", message: "", checkedUrl: "" };
}

export default {
  name: "SettingsPortals",
  components: {
    "fts-spinner": FTSSpinner,
    "fts-dropdown-select": FTSDropdownSelect,
  },
  props: {
    workspaceId: [String, Number],
  },
  data() {
    return {
      loading: true,
      loaded: false,
      notEnabled: false,
      loadError: "",
      error: "",
      allowedOrigins: [],
      portals: [],
      busyId: "",
      now: Date.now(),
      icons: PORTAL_ICONS,
      nameMax: PORTAL_NAME_MAX,
      dialog: { open: false, id: "", saving: false, error: "", original: null },
      form: emptyForm(),
      fieldErrors: {},
      check: emptyCheck(),
      debug: emptyDebug(),
    };
  },
  computed: {
    ready() {
      return this.loaded && !this.notEnabled && !this.loadError && !this.loading;
    },
    rows() {
      return this.portals
        .slice()
        .sort((a, b) => (Number(a.sequence) || 0) - (Number(b.sequence) || 0))
        .map((p) => ({
          id: p.id,
          name: p.name || "Portal",
          icon: portalIcon(p.icon),
          address: addressLabel(p.url),
          audience: audienceText(p.visibility),
          showsOn: showsOnText(p),
          enabled: Boolean(p.enabled),
          // false only when the server says so: it could not reach the page to check it (older servers send nothing)
          checked: p.checked !== false,
          lastOpened: lastOpenedText(p.lastOpenedAt, p.openSessions, this.now),
          portal: p,
        }));
    },
    addressHelpText() {
      return addressHelp(this.allowedOrigins);
    },
    debugRows() {
      return this.debug.grants
        .filter((g) => g && g.id)
        .map((g) => {
          const name = String(g.userName || g.userId || "A workspace admin");
          return {
            id: String(g.id),
            who: g.mine ? name + " (you)" : name,
            port: originPort(g.origin),
            until: untilText(g.expiresAt, this.now),
            grant: g,
          };
        });
    },
    addressInvalid() {
      return Boolean(this.fieldErrors.url || this.check.state === "error");
    },
    /** A real launch for the admin: only for a saved portal that is on and shown on the web. */
    canTest() {
      const saved = this.dialog.original;
      return Boolean(this.dialog.id && saved && saved.enabled && saved.showOnWeb);
    },
  },
  methods: {
    async load() {
      this.loading = !this.loaded;
      this.loadError = "";
      const workspaceId = this.workspaceId;
      try {
        const res = await api.listPortals();
        if (workspaceId !== this.workspaceId) return;
        const data = (res && res.data) || {};
        this.allowedOrigins = Array.isArray(data.allowedOrigins) ? data.allowedOrigins : [];
        this.portals = Array.isArray(data.portals) ? data.portals : [];
        this.notEnabled = false;
        this.now = Date.now();
        this.loaded = true;
      } catch (err) {
        if (workspaceId !== this.workspaceId) return;
        const status = err && err.response && err.response.status;
        // 404 = switched off on this server (not_enabled), or a server without the feature at all.
        if (status === 404) this.notEnabled = true;
        else this.loadError = errorMessage(err, "The portals are not available right now.");
      } finally {
        this.loading = false;
      }
    },

    /** The admin's own side menu follows a change at once (other members' on their next workspace load). */
    syncMenu() {
      if (String(clientStore.getters.workspaceId) !== String(this.workspaceId)) return;
      clientStore.commit("setWorkspacePortals", bootstrapPortals(this.portals));
    },
    upsert(portal) {
      if (!portal || !portal.id) return;
      const at = this.portals.findIndex((p) => p.id === portal.id);
      if (at >= 0) this.portals.splice(at, 1, { ...this.portals[at], ...portal });
      else this.portals.push(portal);
      this.syncMenu();
    },

    // ---- the row menu ----
    focusMenu() {
      this.$nextTick(() => {
        const first = document.querySelector(".fts-portals-menu button");
        if (first) first.focus();
      });
    },
    closeMenu(row) {
      const menu = this.$refs["menu-" + row.id];
      const dropdown = Array.isArray(menu) ? menu[0] : menu;
      const popper = dropdown && dropdown.$refs && dropdown.$refs.popper;
      if (popper && typeof popper.doClose === "function") popper.doClose();
    },
    rowAction(row, action) {
      if (action === "edit") this.openEdit(row.portal);
      else if (action === "toggle") this.toggle(row.portal);
      else if (action === "end") this.endSessions(row.portal);
      else if (action === "delete") this.remove(row.portal);
    },
    async toggle(portal) {
      if (this.busyId) return;
      const on = !portal.enabled;
      this.busyId = portal.id;
      this.error = "";
      try {
        const res = await api.updatePortal(portal.id, { enabled: on });
        this.upsert((res && res.data) || { ...portal, enabled: on });
        this.$toasted.success(on
          ? portal.name + " switched on."
          : portal.name + " switched off. Anyone who had it open is signed out of it.");
      } catch (err) {
        this.error = errorMessage(err, portal.name + " could not be switched " + (on ? "on." : "off."));
      } finally {
        this.busyId = "";
      }
    },
    async endSessions(portal) {
      if (this.busyId) return;
      const confirmed = await this.$confirmation.show({
        type: "confirm",
        title: "End open sessions of " + portal.name + "?",
        text: "Everyone who has " + portal.name + " open is signed out of it within a minute. "
          + "They can open it again from Inistate.",
        buttons: [
          { title: "Cancel", bind: { text: true } },
          { title: "End sessions", default: true, handler: ({ resolve }) => resolve(true) },
        ],
      });
      if (confirmed !== true) return;
      this.busyId = portal.id;
      this.error = "";
      try {
        const res = await api.endPortalSessions(portal.id);
        const ended = Number(res && res.data && res.data.endedSessions) || 0;
        this.upsert({ ...portal, openSessions: 0 });
        this.$toasted.success(ended === 1
          ? "Ended 1 open session of " + portal.name + "."
          : "Ended " + ended + " open sessions of " + portal.name + ".");
      } catch (err) {
        this.error = errorMessage(err, "The open sessions of " + portal.name + " could not be ended.");
      } finally {
        this.busyId = "";
      }
    },
    async remove(portal) {
      if (this.busyId) return;
      const confirmed = await this.$confirmation.show({
        type: "confirm",
        title: "Delete " + portal.name + "?",
        text: "Members lose it from the side menu and the mobile app, and anyone who has it open is signed "
          + "out of it. The page itself stays where Inistate published it.",
        buttons: [
          { title: "Cancel", bind: { text: true } },
          { title: "Delete", bind: { color: "danger" }, default: true, handler: ({ resolve }) => resolve(true) },
        ],
      });
      if (confirmed !== true) return;
      this.busyId = portal.id;
      this.error = "";
      try {
        await api.deletePortal(portal.id);
        this.portals = this.portals.filter((p) => p.id !== portal.id);
        this.syncMenu();
        this.$toasted.success(portal.name + " deleted.");
      } catch (err) {
        this.error = errorMessage(err, portal.name + " could not be deleted.");
      } finally {
        this.busyId = "";
      }
    },

    // ---- the dialog ----
    openAdd() {
      this.openDialog(null);
    },
    openEdit(portal) {
      this.openDialog(portal);
    },
    /**
     * A hand-over's setup link (portalSetupLink.js): Add portal filled in, or the portal already at that address
     * opened to edit with the link's values, for the admin to check and Save. The link is read once.
     */
    openFromLink() {
      const asked = addLinkOf(this.$route && this.$route.query);
      if (!asked || !this.ready) return;
      if (this.$router) this.$router.replace({ query: {} }).catch(() => {});
      if (asked.workspaceId && asked.workspaceId !== String(this.workspaceId)) {
        this.error = "This setup link is for another workspace (" + asked.workspaceId + "). Switch to it, then open the link again.";
        return;
      }
      this.openDialog(portalAt(this.portals, asked.form.url));
      this.form = { ...this.form, ...asked.form };
      // the page should be uploaded by now: say at once whether Inistate can open it
      if (asked.form.url) this.checkAddress(true);
    },
    openDialog(portal) {
      this.lastFocus = typeof document !== "undefined" ? document.activeElement : null;
      this.form = portal
        ? {
          icon: portal.icon || DEFAULT_PORTAL_ICON,
          name: portal.name || "",
          url: portal.url || "",
          visibility: String(portal.visibility || "").toLowerCase() === "admins" ? "admins" : "everyone",
          showOnWeb: Boolean(portal.showOnWeb),
          showOnApp: Boolean(portal.showOnApp),
        }
        : emptyForm();
      this.fieldErrors = {};
      this.check = emptyCheck();
      this.dialog = { open: true, id: portal ? portal.id : "", saving: false, error: "", original: portal ? { ...portal } : null };
      this.$nextTick(() => {
        if (this.$refs.nameInput) this.$refs.nameInput.focus();
      });
    },
    closeDialog() {
      if (!this.dialog.open) return;
      this.dialog = { open: false, id: "", saving: false, error: "", original: null };
      const back = this.lastFocus;
      this.lastFocus = null;
      this.$nextTick(() => {
        const target = back && document.body.contains(back) ? back : this.$refs.addButton;
        if (target && typeof target.focus === "function") target.focus();
      });
    },
    /** Tab stays inside the dialog while it is open. */
    trapFocus(event) {
      const root = this.$refs.dialog;
      if (!root) return;
      const items = Array.prototype.slice.call(root.querySelectorAll(FOCUSABLE));
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    /** Leaving the field checks it, unless the focus went to Check or Save, which answer it themselves. */
    onAddressBlur(event) {
      const next = event && event.relatedTarget;
      if (next && (next === this.$refs.checkButton || next === this.$refs.saveButton)) return;
      this.checkAddress(false);
    },
    onAddressInput() {
      // A changed address has not been checked yet, and the last refusal no longer applies.
      this.check = emptyCheck();
      if (this.fieldErrors.url) this.fieldErrors = { ...this.fieldErrors, url: "" };
    },
    /**
     * The server's address rules plus the page's opt-in. On leaving the field only when the address is
     * new (a saved one was checked when it was saved); the Check button always asks.
     */
    async checkAddress(force) {
      const url = String(this.form.url || "").trim();
      if (!url) {
        this.check = emptyCheck();
        return;
      }
      // Pressing Check takes the focus off the field first: that blur already asked about this address.
      if (this.check.state === "checking" && this.check.checkedUrl === url) return;
      if (!force) {
        const saved = this.dialog.original;
        if (url === this.check.checkedUrl) return;
        if (saved && url === saved.url) return;
      }
      this.check = { state: "checking", message: "", checkedUrl: url };
      try {
        const res = await api.checkPortalAddress(url);
        if (String(this.form.url || "").trim() !== url) return;
        const data = (res && res.data) || {};
        this.check = data.checked === false
          ? { state: "unchecked", message: data.warning || "Inistate's server could not reach this page to check it. It can still be saved, marked as not checked.", checkedUrl: url }
          : { state: "ok", message: "", checkedUrl: url };
        if (this.fieldErrors.url) this.fieldErrors = { ...this.fieldErrors, url: "" };
      } catch (err) {
        if (String(this.form.url || "").trim() !== url) return;
        this.check = { state: "error", message: errorMessage(err, "That address could not be checked."), checkedUrl: url };
      }
    },
    /** Only what changed goes up on an edit: an unchanged address is not fetched and checked again. */
    payload() {
      const f = this.form;
      const fields = {
        name: String(f.name || "").trim(),
        icon: f.icon || DEFAULT_PORTAL_ICON,
        visibility: f.visibility === "admins" ? "admins" : "everyone",
        showOnWeb: Boolean(f.showOnWeb),
        showOnApp: Boolean(f.showOnApp),
      };
      const url = String(f.url || "").trim();
      const saved = this.dialog.original;
      if (!saved) return { ...fields, url, enabled: true };
      if (url !== saved.url) fields.url = url;
      return fields;
    },
    /** True when this edit ends the portal's open sessions on the server. */
    endsSessions(fields) {
      const saved = this.dialog.original;
      if (!saved) return false;
      return Boolean(
        fields.url !== undefined
        || (fields.visibility === "admins" && String(saved.visibility).toLowerCase() !== "admins")
        || (saved.showOnWeb && !fields.showOnWeb)
        || (saved.showOnApp && !fields.showOnApp)
      );
    },
    async save() {
      if (this.dialog.saving) return;
      const problems = formProblems(this.form);
      this.fieldErrors = problems;
      this.dialog.error = "";
      if (Object.keys(problems).length) return;

      const fields = this.payload();
      const saved = this.dialog.original;
      this.dialog.saving = true;
      try {
        const res = saved
          ? await api.updatePortal(saved.id, fields)
          : await api.createPortal(fields);
        const portal = (res && res.data) || null;
        if (portal) this.upsert(portal);
        const name = (portal && portal.name) || fields.name;
        let done = saved ? name + " saved." : name + " added.";
        if (saved && Number(saved.openSessions) > 0 && this.endsSessions(fields)) {
          done = name + " saved. Anyone who had it open is signed out of it.";
        }
        this.$toasted.success(done);
        this.dialog.saving = false;
        this.closeDialog();
      } catch (err) {
        this.dialog.saving = false;
        const message = errorMessage(err, saved ? "The portal was not saved." : "The portal was not added.");
        const field = fieldOfError(errorCode(err));
        if (field) this.fieldErrors = { ...this.fieldErrors, [field]: message };
        else this.dialog.error = message;
      }
    },
    /** A real launch, in a new tab, through the same route members use. */
    openToTest() {
      if (!this.dialog.id) return;
      window.open(portalHref(this.dialog.id), "_blank", "noopener");
    },

    // ---- debug sessions (§3.4) ----
    async loadDebug() {
      const workspaceId = this.workspaceId;
      this.debug.loading = !this.debug.loaded;
      this.debug.error = "";
      try {
        const res = await api.listDebugGrants();
        if (workspaceId !== this.workspaceId) return;
        const data = (res && res.data) || {};
        this.debug.grants = Array.isArray(data.grants) ? data.grants : [];
        this.debug.off = false;
        this.debug.loaded = true;
        this.now = Date.now();
      } catch (err) {
        if (workspaceId !== this.workspaceId) return;
        const status = err && err.response && err.response.status;
        // 404 = debugging is off on this server (not_enabled), or a server without it at all.
        if (status === 404) this.debug.off = true;
        else this.debug.error = errorMessage(err, "The debug sessions are not available right now.");
      } finally {
        this.debug.loading = false;
      }
    },
    async endDebug(row) {
      if (this.debug.busyId) return;
      const confirmed = await this.$confirmation.show({
        type: "confirm",
        title: "End the debug session on port " + row.port + "?",
        text: "The page on " + row.who + "'s computer loses its Inistate session within a minute. "
          + "Running /portal try again asks for a new one.",
        buttons: [
          { title: "Cancel", bind: { text: true } },
          { title: "End session", default: true, handler: ({ resolve }) => resolve(true) },
        ],
      });
      if (confirmed !== true) return;
      this.debug.busyId = row.id;
      this.error = "";
      try {
        await api.endDebugGrant(row.id);
        this.debug.grants = this.debug.grants.filter((g) => String(g.id) !== row.id);
        this.$toasted.success("Debug session on port " + row.port + " ended.");
      } catch (err) {
        if (errorCode(err) === "grant_not_found") {
          // Already over (its time ran out, or it was ended elsewhere): the row was out of date.
          this.debug.grants = this.debug.grants.filter((g) => String(g.id) !== row.id);
          this.$toasted.success("The debug session on port " + row.port + " had already ended.");
        } else {
          this.error = errorMessage(err, "The debug session on port " + row.port + " could not be ended.");
        }
      } finally {
        this.debug.busyId = "";
      }
    },
  },
  watch: {
    workspaceId() {
      this.loaded = false;
      this.portals = [];
      this.debug = emptyDebug();
      this.closeDialog();
      this.load();
      this.loadDebug();
    },
  },
  created() {
    this.lastFocus = null;
    this.load().then(() => this.openFromLink());
    this.loadDebug();
  },
};
</script>

<style lang="scss" scoped>
.fts-portals-empty {
  margin: 0;
  color: var(--fts-settings-mute);
}

.fts-portals-retry {
  margin-left: 4px;
}

/* A table wider than a phone scrolls inside its card instead of pushing the page sideways. Positioned,
   so the visually hidden "Actions" heading stays inside it too. */
.fts-portals-scroll {
  position: relative;
  overflow-x: auto;
}

.fts-portals-table {
  min-width: 720px;

  td {
    vertical-align: middle;
  }
}

.fts-portals-debug-table {
  min-width: 420px;
}

.fts-portals-name {
  display: inline-flex;
  align-items: center;
  gap: 8px;

  strong {
    font-weight: 600;
  }
}

.fts-portals-name-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  background: var(--fts-settings-accent-soft);
  color: var(--fts-settings-accent);
}

.fts-portals-address {
  word-break: break-all;
}

.fts-portals-nowrap {
  white-space: nowrap;
}

.fts-portals-sr {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.fts-portals-more {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--fts-settings-mute);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: var(--fts-settings-ground);
    color: var(--fts-settings-ink);
  }

  &:focus-visible {
    outline: 2px solid var(--fts-settings-accent);
    outline-offset: 1px;
  }

  &:disabled {
    opacity: 0.5;
  }
}

/* ---- the dialog ---- */
.fts-portals-overlay {
  position: fixed;
  inset: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
  background: rgba(33, 33, 33, 0.45);
}

.fts-portals-dialog {
  box-sizing: border-box;
  width: 100%;
  max-width: 560px;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  border-radius: 12px;
  background: var(--inistate-color-shade-white);
  color: var(--fts-settings-ink);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
}

.fts-portals-dialog-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px 4px;

  h2 {
    flex: 1;
    margin: 0;
    font-size: 1.125rem;
    line-height: 1.625rem;
    font-weight: 600;
  }
}

.fts-portals-close {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--fts-settings-mute);
  cursor: pointer;

  &:hover {
    background: var(--fts-settings-ground);
  }
}

.fts-portals-form {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 12px 24px 20px;
}

.fts-portals-icons {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.fts-portals-icon-choice {
  width: 40px;
  height: 40px;
  border: 1px solid var(--fts-settings-line);
  border-radius: 8px;
  background: var(--inistate-color-shade-white);
  color: var(--fts-settings-mute);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: var(--fts-settings-ground);
  }

  &.is-on {
    border-color: var(--fts-settings-accent);
    box-shadow: 0 0 0 1px var(--fts-settings-accent);
    background: var(--fts-settings-accent-soft);
    color: var(--fts-settings-accent);
  }

  &:focus-visible {
    outline: 2px solid var(--fts-settings-accent);
    outline-offset: 2px;
  }
}

.fts-portals-input {
  width: 100%;
  min-width: 0;
  height: 40px;

  &[aria-invalid="true"] {
    border-color: var(--inistate-color-red-600);
  }
}

.fts-portals-address-row {
  display: flex;
  gap: 8px;

  .fts-portals-input {
    flex: 1;
  }
}

.fts-portals-check {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 500;
  background: var(--inistate-color-green-50);
  color: var(--inistate-color-green-800);

  &.is-warn {
    align-items: flex-start;
    background: var(--inistate-color-orange-50);
    color: var(--inistate-color-orange-900);
  }
}

.fts-portals-unchecked {
  display: block;
  margin-top: 4px;
  font-size: 0.75rem;
  color: var(--inistate-color-orange-900);
}

.fts-portals-fieldset {
  margin: 0;
  padding: 0;
  border: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;

  legend {
    padding: 0;
    margin-bottom: 8px;
  }
}

.fts-portals-choice {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 0.875rem;
  font-weight: 400;
  cursor: pointer;

  input {
    width: 16px;
    height: 16px;
    margin: 0;
    accent-color: var(--fts-settings-accent);
  }
}

.fts-portals-dialog-foot {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid var(--fts-settings-hair);
}

.fts-portals-spacer {
  flex: 1;
}

@media (max-width: 560px) {
  .fts-portals-dialog-head {
    padding: 16px 16px 4px;
  }

  .fts-portals-form {
    padding: 12px 16px 16px;
  }
}
</style>

<style lang="scss">
/* The row menu is drawn on <body>, outside the settings frame. */
.fts-portals-menu {
  display: flex;
  flex-direction: column;
  min-width: 180px;

  /* The option class brings the padding, size and hover; these make a <button> look like it. */
  > button {
    border: none;
    background: transparent;
    font-family: inherit;
    text-align: left;
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid var(--fts-primary, currentColor);
      outline-offset: -2px;
    }
  }

  > .fts-portals-menu-danger {
    color: var(--fts-danger, #d32f2f);
  }
}
</style>
